import Immutable from 'immutable'
import { push } from 'react-router-redux'
import { AnyAction, Dispatch } from 'redux'

import { actionTypes } from 'actions/actionTypes'
import { basketPostcodeChange } from 'actions/basket'
import loginActions from 'actions/login'
import { menuLoadBoxPrices } from 'actions/menu'
import { redirect } from 'actions/redirect'
import tempActions from 'actions/temp'
import { trackUTMAndPromoCode } from 'actions/tracking'
import {
  completeWizardPostcode,
  clickSeeThisWeeksMenu,
  signupCheckAccountNewCustomer,
  signupCheckAccountExistingCustomer,
  signupApplyVoucher,
  deliveryInstructionDetailsClick,
  promoCodeInvalid,
} from 'actions/trackingKeys'
import routes, { client } from 'config/routes'
import { signupConfig } from 'routes/Signup/signupConfig'
import { stepByName } from 'routes/Signup/signupUtils'
import { getDeliveryDetailsInstructions } from 'selectors/deliveryDetails'
import { getUTMAndPromoCode } from 'selectors/tracking'

import { GetState } from './models/SignupStore'

export function signupStepsReceive(stepNames: string) {
  return {
    type: actionTypes.SIGNUP_STEPS_RECEIVE,
    stepNames,
  }
}

export function signupSetStep(step: any) {
  return (dispatch: Dispatch, getState: GetState) => {
    const signupState = getState().signup
    const stepNames = signupState.getIn(['wizard', 'stepNames'])

    if (step) {
      const newStepNumber = stepNames.findIndex((stepName: string) => stepName === step.get('name'))
      const isLastStep = newStepNumber === stepNames.size - 1

      dispatch({
        type: actionTypes.SIGNUP_STEP_SET,
        currentStepName: step.get('name'),
        currentStepNumber: newStepNumber,
        isLastStep,
        trackingData: {
          type: actionTypes.SIGNUP_STEP_SET,
          step: step.get('slug'),
          stepName: step.get('name'),
        },
      })
    }
  }
}

export function signupTracking() {
  return (dispatch: Dispatch, getState: GetState) => {
    const { basket } = getState()
    const postcode = basket.get('postcode')
    const numAdults = basket.get('numAdults')
    const numPortions = basket.get('numPortions')
    const date = basket.get('date')
    const slotId = basket.get('slotId')

    dispatch({
      type: actionTypes.SIGNUP_TRACKING,
      trackingData: {
        actionType: actionTypes.SIGNUP_TRACKING,
        postcode,
        numAdults,
        numPortions,
        date,
        slotId,
      },
    })
  }
}

/**
 * Redirects application to Signup exact step.
 */
export function signupNextStep(stepName: string | number) {
  return async (dispatch: Dispatch, getState: GetState) => {
    const step = stepByName(stepName)
    if (step) {
      const state = getState()
      const signupState = state.signup
      const isCurrentlyTheLastStep = signupState.getIn(['wizard', 'isLastStep'])
      const lastWizardStep = signupState.getIn(['wizard', 'stepNames']).last()
      const slug = step.get('slug')
      if (isCurrentlyTheLastStep && (step.get('name') === lastWizardStep || !slug)) {
        dispatch(signupTracking() as unknown as AnyAction)

        const path = `${routes.client.signup}/${signupConfig.sellThePropositionPageSlug}`

        return dispatch(redirect(path))
      }

      try {
        const { search } = getState().routing.locationBeforeTransitions
        dispatch(push(`${client.signup}/${slug}${search}`))
      } catch (e) {
        dispatch(push(`${client.signup}/${slug}`))
      }
    }

    return null
  }
}

export function signupGoToMenu() {
  return (dispatch: Dispatch) => {
    dispatch(trackUTMAndPromoCode(clickSeeThisWeeksMenu) as unknown as AnyAction)
    dispatch(redirect(routes.client.menu))
    dispatch({ type: actionTypes.WIZARD_SEEN })
  }
}

export const trackSignupWizardAction =
  (type: any, additionalData = {}) =>
  (dispatch: Dispatch, getState: GetState) => {
    const { promoCode, UTM } = getUTMAndPromoCode(getState())

    dispatch({
      type,
      trackingData: {
        actionType: type,
        ...UTM,
        promoCode,
        ...additionalData,
      },
    })
  }

export function signupChangePostcode(postcode: string, nextStepName: string | number) {
  return async (dispatch: Dispatch, getState: GetState) => {
    await dispatch(basketPostcodeChange(postcode) as unknown as AnyAction)

    if (!getState().error.get(actionTypes.BOXSUMMARY_DELIVERY_DAYS_RECEIVE, false)) {
      dispatch(
        trackSignupWizardAction(completeWizardPostcode, { postcode }) as unknown as AnyAction,
      )
      signupNextStep(nextStepName)(dispatch, getState)
    }
  }
}

export const signupChangeTempPostcode = (postcode: string) =>
  tempActions.temp('postcode', postcode.toUpperCase())

export const signupDismissDiscountAppliedBar = () => ({
  type: actionTypes.SIGNUP_DISMISS_DISCOUNT_APPLIED_BAR,
})

export const signupShowDiscountAppliedBar = () => ({
  type: actionTypes.SIGNUP_SHOW_DISCOUNT_APPLIED_BAR,
})

export const signupCheckAccountGoToBoxPrices = () => (dispatch: Dispatch) => {
  dispatch(menuLoadBoxPrices() as unknown as AnyAction)
  dispatch(trackUTMAndPromoCode(signupCheckAccountNewCustomer) as unknown as AnyAction)
  dispatch(redirect(`${routes.client.signup}/box-size`))
}

export const signupCheckAccountLogin = () => (dispatch: Dispatch) => {
  dispatch(trackUTMAndPromoCode(signupCheckAccountExistingCustomer) as unknown as AnyAction)

  const { loginVisibilityChange } = loginActions
  dispatch(loginVisibilityChange(true))
}

export const signupApplyVoucherGoToDeliveries = () => (dispatch: Dispatch) => {
  dispatch(trackUTMAndPromoCode(signupApplyVoucher) as unknown as AnyAction)
  dispatch(redirect(`${routes.client.myDeliveries}`))
}

export const trackDeliveryInstructionSelection = () => (dispatch: Dispatch, getState: GetState) => {
  const deliveryInstruction = getDeliveryDetailsInstructions(getState())

  dispatch({
    type: actionTypes.TRACKING,
    trackingData: {
      actionType: deliveryInstructionDetailsClick,
      selection: deliveryInstruction,
    },
  })
}

export const trackingPromoCodeInvalid = (error: any) => ({
  type: actionTypes.TRACKING,
  trackingData: {
    actionType: promoCodeInvalid,
    error,
  },
})

export const signupSetSignupMotivations =
  (signupMotivations: Immutable.List<string>) => (dispatch: Dispatch) => {
    dispatch({
      type: actionTypes.SIGNUP_SET_SIGNUP_MOTIVATIONS,
      signupMotivations,
    })
  }
